import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import type { MeasurementType } from '@/features/patient-record/types';

import { convertField, type FieldDraft } from './measurementDraft';
import {
  MEASUREMENT_UNIT,
  unitLabel,
  unitOptionsFor,
  type MeasurementUnit,
} from './unitConversion';

interface MeasurementFieldRowProps {
  readonly label: string;
  readonly type: MeasurementType;
  readonly field: FieldDraft;
  readonly onChange: (next: FieldDraft) => void;
}

/**
 * One measurement row: value + unit picker. Picking `ft + in` splits the
 * value into two boxes (PTV2-1560) — the way a height is actually quoted —
 * and changing the unit converts what's already typed instead of
 * re-reading the number as the new unit.
 */
export function MeasurementFieldRow({
  label,
  type,
  field,
  onChange,
}: MeasurementFieldRowProps): ReactElement {
  const { t } = useTranslation();
  const isFeetInches = field.unit === MEASUREMENT_UNIT.ftIn;

  return (
    <div className="grid grid-cols-[8rem_1fr_6rem] gap-2 items-center">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {isFeetInches ? (
        <div className="grid grid-cols-2 gap-2">
          <NumberBox
            value={field.value}
            suffix="ft"
            ariaLabel={`${label} ${t('patient.medicalHistory.measurement.feetLabel')}`}
            onChange={(value) => {
              onChange({ ...field, value });
            }}
          />
          <NumberBox
            value={field.inches}
            suffix="in"
            ariaLabel={`${label} ${t('patient.medicalHistory.measurement.inchesLabel')}`}
            onChange={(inches) => {
              onChange({ ...field, inches });
            }}
          />
        </div>
      ) : (
        <NumberBox
          value={field.value}
          suffix={null}
          ariaLabel={label}
          onChange={(value) => {
            onChange({ ...field, value });
          }}
        />
      )}
      <Select
        value={field.unit}
        onChange={(event) => {
          onChange(convertField(field, event.target.value as MeasurementUnit));
        }}
        className="h-8 text-xs"
        aria-label={`${label} ${t('patient.medicalHistory.measurement.unitsLabel')}`}
      >
        {unitOptionsFor(type).map((unit) => (
          <option key={unit} value={unit}>
            {unitLabel(unit)}
          </option>
        ))}
      </Select>
    </div>
  );
}

interface NumberBoxProps {
  readonly value: string;
  /** Unit hint pinned inside the box — only used by the feet/inches pair. */
  readonly suffix: string | null;
  readonly ariaLabel: string;
  readonly onChange: (next: string) => void;
}

function NumberBox({
  value,
  suffix,
  ariaLabel,
  onChange,
}: NumberBoxProps): ReactElement {
  return (
    <div className="relative">
      <Input
        type="number"
        step="0.1"
        min="0"
        inputMode="decimal"
        value={value}
        placeholder="—"
        aria-label={ariaLabel}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={`h-8 text-sm tabular-nums ${suffix === null ? '' : 'pr-7'}`}
      />
      {suffix !== null && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[10px] text-muted-foreground"
        >
          {suffix}
        </span>
      )}
    </div>
  );
}
